import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, Hash, MessageCircle, Mail, MapPin, Phone, Zap, User, Trash2, Edit, HandCoins, Settings, Bell, Flag } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import PhotoLightbox from "@/components/PhotoLightbox";
import WatchListingModal from "@/components/WatchListingModal";
import WatchCategoryModal from "@/components/WatchCategoryModal";
import ReportListingModal from "@/components/ReportListingModal";
import CraftBoltBanner from "@/components/CraftBoltBanner";
import { CATEGORY_NAMES, SUBCATEGORY_NAMES, formatDateCzk, formatPriceCzk } from "@/lib/categories";
import { useSEO } from "@/lib/useSEO";
import { isMyAd } from "@/lib/myAds";
import { contactSpamReason, isTopActive } from "@/lib/moderation";

export default function AdDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const topQuery = searchParams.get("top");
  const sessionId = searchParams.get("session_id");

  const [ad, setAd] = useState(null);
  const [owner, setOwner] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [topStatus, setTopStatus] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/listings/${id}`);
      setAd(data.listing);
      setActivePhoto(0);
      try {
        const ownerRes = await api.get(`/users/${data.listing.owner_id}`);
        setOwner(ownerRes.data.user);
      } catch (_) {}
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll Stripe TOP payment status
  useEffect(() => {
    if (topQuery !== "success" || !sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/top/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setTopStatus("paid");
          setSearchParams({});
          load();
          return;
        }
        if (data.status === "expired") {
          setTopStatus("expired");
          return;
        }
        setTopStatus("pending");
        if (attempts++ < 8) setTimeout(poll, 2000);
      } catch (e) {
        if (attempts++ < 8) setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topQuery, sessionId]);

  const productLd = ad ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        "name": ad.title,
        "description": ad.description?.slice(0, 500),
        "image": ad.photos?.slice(0, 4),
        "category": CATEGORY_NAMES[ad.category] || "",
        "brand": { "@type": "Brand", "name": "STAVBOŠ" },
        "offers": {
          "@type": "Offer",
          "url": typeof window !== "undefined" ? window.location.href : "",
          "priceCurrency": "CZK",
          "price": ad.price ?? 0,
          "availability": "https://schema.org/InStock",
          "itemCondition": "https://schema.org/UsedCondition",
          "areaServed": "CZ",
        },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Domů", "item": typeof window !== "undefined" ? window.location.origin + "/" : "https://stavbos.cz/" },
          ...(ad.category && CATEGORY_NAMES[ad.category]
            ? [{ "@type": "ListItem", "position": 2, "name": CATEGORY_NAMES[ad.category], "item": (typeof window !== "undefined" ? window.location.origin : "https://stavbos.cz") + "/kategorie/" + ad.category }]
            : []),
          { "@type": "ListItem", "position": ad.category && CATEGORY_NAMES[ad.category] ? 3 : 2, "name": ad.title, "item": typeof window !== "undefined" ? window.location.href : "" },
        ],
      },
    ],
  } : null;
  useSEO({
    title: ad?.title,
    description: ad?.description?.slice(0, 160),
    image: ad?.photos?.[0],
    jsonLd: productLd,
  });

  if (loading) {
    return (
      <Layout>
        <div className="stb-card h-96 animate-pulse" data-testid="ad-loading" />
      </Layout>
    );
  }
  if (err || !ad) {
    return (
      <Layout>
        <div className="stb-card p-6 text-center" data-testid="ad-error">
          <h2 className="stb-h text-2xl text-white">Inzerát nenalezen</h2>
          <p className="text-[#A3A3A3] mt-2">{err}</p>
          <Link to="/" className="stb-btn stb-btn-primary mt-4 inline-flex">
            <ArrowLeft size={14} /> Zpět na inzeráty
          </Link>
        </div>
      </Layout>
    );
  }

  const isOwner = isMyAd(id);
  const photo = ad.photos[activePhoto];
  const priceType = ad.price_type || (ad.price_negotiable ? "negotiable" : "fixed");

  return (
    <Layout>
      <button
        onClick={() => navigate(-1)}
        className="stb-btn stb-btn-ghost text-[#0E0E0E] mb-3"
        data-testid="ad-back-btn"
      >
        <ArrowLeft size={14} /> Zpět
      </button>

      {topQuery === "success" && (
        <div className="stb-card p-3 mb-4 border-[#22C55E]" data-testid="top-success-banner">
          <p className="text-[#22C55E] stb-h">
            {topStatus === "paid"
              ? "Inzerát byl TOPnut! Posunul se nahoru."
              : topStatus === "expired"
                ? "Platba vypršela."
                : "Kontroluji platbu…"}
          </p>
        </div>
      )}
      {topQuery === "cancel" && (
        <div className="stb-card p-3 mb-4 border-[#FFB800]" data-testid="top-cancel-banner">
          <p className="text-[#FFB800] stb-h">Platba zrušena. Inzerát zůstal stejný.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="stb-card overflow-hidden">
            <div className="bg-[#0E0E0E] relative">
              {photo ? (
                <img
                  src={photo}
                  alt={ad.title}
                  className="stb-photo !h-[420px] !border-0 !rounded-none"
                  onClick={() => setLightboxOpen(true)}
                  data-testid="ad-photo-main"
                />
              ) : (
                <div className="h-[420px] flex items-center justify-center stb-mono text-[#4A4A4A]">
                  BEZ FOTOGRAFIE
                </div>
              )}
              {isTopActive(ad) && (
                <span className="absolute top-3 left-3 stb-badge-top stb-pulse" data-testid="ad-top-badge">
                  <Zap size={12} /> TOP
                </span>
              )}
            </div>
            {ad.photos.length > 1 && (
              <div className="p-3 grid grid-cols-5 sm:grid-cols-8 gap-2" data-testid="ad-photo-thumbs">
                {ad.photos.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    className={`stb-photo-thumb ${activePhoto === i ? "active" : ""}`}
                    data-testid={`ad-photo-thumb-${i}`}
                  >
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="p-5 border-t-2 border-[#262626]">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h1 className="stb-h text-3xl md:text-4xl text-white leading-tight" data-testid="ad-title">
                    {ad.title}
                  </h1>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {CATEGORY_NAMES[ad.category] && (
                      <span className="stb-chip">{CATEGORY_NAMES[ad.category]}</span>
                    )}
                    {ad.subcategory && (
                      <span className="stb-chip border-[#FF6B00] text-[#FF6B00]">
                        {SUBCATEGORY_NAMES[ad.subcategory]}
                      </span>
                    )}
                  </div>
                </div>
                <PriceTag priceType={priceType} price={ad.price} />
              </div>

              <div className="mt-4 stb-mono text-xs text-[#A3A3A3] flex flex-wrap gap-4">
                <span className="inline-flex items-center gap-1">
                  <Eye size={12} /> {ad.views} zhlédnutí
                </span>
                <span>· {formatDateCzk(ad.created_at)}</span>
                {ad.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={12} /> {ad.location}
                  </span>
                )}
                {ad.serial_number && (
                  <span className="inline-flex items-center gap-1">
                    <Hash size={12} /> SN: {ad.serial_number}
                  </span>
                )}
                {ad.ean_code && (
                  <span className="inline-flex items-center gap-1">
                    EAN: {ad.ean_code}
                  </span>
                )}
                {ad.vin_code && (
                  <span className="inline-flex items-center gap-1">
                    VIN: {ad.vin_code}
                  </span>
                )}
                {ad.year_of_manufacture && (
                  <span className="inline-flex items-center gap-1">
                    Rok: {ad.year_of_manufacture}
                  </span>
                )}
                {ad.machine_hours != null && (
                  <span className="inline-flex items-center gap-1">
                    Mth: {Number(ad.machine_hours).toLocaleString("cs-CZ")}
                  </span>
                )}
                {ad.weight_kg != null && (
                  <span className="inline-flex items-center gap-1">
                    Hmotnost: {Number(ad.weight_kg).toLocaleString("cs-CZ")} kg
                  </span>
                )}
              </div>

              <hr className="stb-divider" />

              <h3 className="stb-label">Popis</h3>
              <p className="text-white whitespace-pre-wrap leading-relaxed mt-2" data-testid="ad-description">
                {ad.description}
              </p>

              <hr className="stb-divider" />
              {!isOwner && (
                <div className="flex flex-wrap gap-2">
                  <button
                    className="stb-btn stb-btn-secondary"
                    onClick={() => navigate(`/sprava-inzeratu/${ad.id}`)}
                    data-testid="ad-manage-btn"
                    title="Pro úpravu zadej e-mail a heslo k inzerátu"
                  >
                    <Edit size={14} /> Jsem vlastník – spravovat
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="space-y-4">
          <SellerCard ad={ad} owner={owner} />
          {isOwner ? (
            <OwnerActionsBox ad={ad} />
          ) : (
            <ContactBox ad={ad} priceType={priceType} />
          )}
          <CraftBoltBanner message="Potřebuješ tento materiál dovézt nebo zabudovat? Najdi si řemeslníka na CraftBolt." />
        </aside>
      </div>

      {lightboxOpen && ad.photos.length > 0 && (
        <PhotoLightbox
          photos={ad.photos}
          index={activePhoto}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setActivePhoto}
        />
      )}
    </Layout>
  );
}

function PriceTag({ priceType, price }) {
  if (priceType === "free") {
    return <span className="stb-badge-price stb-badge-free !text-xl" data-testid="ad-price">ZDARMA</span>;
  }
  if (priceType === "negotiable") {
    return <span className="stb-badge-price stb-badge-negotiable !text-xl" data-testid="ad-price">DOHODOU</span>;
  }
  if (priceType === "offer") {
    return <span className="stb-badge-price stb-badge-offer !text-xl" data-testid="ad-price">NABÍDNI CENU</span>;
  }
  return <span className="stb-badge-price !text-xl" data-testid="ad-price">{formatPriceCzk(price, false)}</span>;
}

function OwnerActionsBox({ ad }) {
  const navigate = useNavigate();
  return (
    <div className="stb-card p-4 space-y-2 border-[#FF6B00]" data-testid="owner-actions-box">
      <h3 className="stb-label inline-flex items-center gap-2">
        <Settings size={12} className="text-[#FF6B00]" /> Tvůj inzerát
      </h3>
      <p className="text-sm text-[#A3A3A3]">
        Tento inzerát jsi vložil/a na tomto zařízení. Spravuj ho, topni, nebo si přečti zprávy od zájemců.
      </p>
      <div className="grid gap-2 mt-2">
        <button
          onClick={() => navigate(`/sprava-inzeratu/${ad.id}`)}
          className="stb-btn stb-btn-secondary w-full"
          data-testid="owner-manage-btn"
        >
          <Edit size={14} /> Upravit inzerát
        </button>
        <button
          onClick={() => navigate(`/sprava-inzeratu/${ad.id}/zpravy`)}
          className="stb-btn stb-btn-secondary w-full"
          data-testid="owner-messages-btn"
        >
          <MessageCircle size={14} /> Zprávy zájemců
        </button>
        <button
          onClick={() => navigate(`/sprava-inzeratu/${ad.id}?action=top`)}
          className="stb-btn stb-btn-primary w-full"
          data-testid="owner-top-btn"
        >
          <Zap size={14} /> TOP za 49 Kč
        </button>
      </div>
    </div>
  );
}

function SellerCard({ ad, owner }) {
  return (
    <div className="stb-card p-4" data-testid="seller-card">
      <h3 className="stb-label">Prodejce</h3>
      <div className="flex items-center gap-3 mt-2">
        {ad.owner_avatar || owner?.avatar_data_url ? (
          <img src={ad.owner_avatar || owner.avatar_data_url} alt="" className="w-16 h-16 object-cover border-2 border-[#FF6B00]" />
        ) : (
          <div className="w-16 h-16 bg-[#0E0E0E] border-2 border-[#262626] inline-flex items-center justify-center">
            <User size={28} className="text-[#A3A3A3]" />
          </div>
        )}
        <div>
          <div className="stb-h text-xl text-white" data-testid="seller-name">{ad.contact_name}</div>
          <div className="stb-mono text-xs text-[#A3A3A3]">{owner?.email_verified ? "Ověřený email" : "STAVBOŠ"}</div>
        </div>
      </div>
    </div>
  );
}

function ContactBox({ ad, priceType }) {
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  const [watchCatOpen, setWatchCatOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const canOfferPrice = priceType !== "free";

  return (
    <div className="stb-card p-4 space-y-2" data-testid="contact-box">
      <h3 className="stb-label">Kontakt</h3>
      <button
        onClick={() => setChatOpen(true)}
        className="stb-btn stb-btn-primary w-full justify-center"
        data-testid="contact-chat-btn"
      >
        <MessageCircle size={16} /> Spustit chat
      </button>
      <ContactDialog ad={ad} initialOfferedPrice={null} />
      {canOfferPrice && (
        <button
          type="button"
          onClick={() => setOfferOpen(true)}
          className="stb-btn stb-btn-secondary w-full justify-center"
          data-testid="offer-price-btn"
        >
          <HandCoins size={16} /> Nabídnout {priceType === "fixed" ? "jinou" : ""} cenu
        </button>
      )}
      <button
        type="button"
        onClick={() => setWatchOpen(true)}
        className="stb-btn stb-btn-secondary w-full justify-center"
        data-testid="watch-listing-btn"
      >
        <Bell size={16} /> Sledovat inzerát
      </button>
      <button
        type="button"
        onClick={() => setWatchCatOpen(true)}
        className="stb-btn stb-btn-ghost w-full justify-center"
        data-testid="watch-category-btn"
      >
        <Bell size={14} /> Sledovat podobné v kategorii
      </button>
      <button
        type="button"
        onClick={() => setReportOpen(true)}
        className="stb-btn stb-btn-ghost w-full justify-center text-xs"
        data-testid="report-listing-btn"
      >
        <Flag size={12} /> Nahlásit inzerát
      </button>

      {offerOpen && <PriceOfferModal ad={ad} onClose={() => setOfferOpen(false)} />}
      {chatOpen && (
        <AnonymousChatStart ad={ad} onClose={() => setChatOpen(false)} navigate={navigate} />
      )}
      {watchOpen && (
        <WatchListingModal
          listingId={ad.id}
          listingTitle={ad.title}
          onClose={() => setWatchOpen(false)}
        />
      )}
      {watchCatOpen && (
        <WatchCategoryModal
          category={ad.category}
          subcategory={ad.subcategory}
          location={ad.location}
          onClose={() => setWatchCatOpen(false)}
        />
      )}
      {reportOpen && (
        <ReportListingModal
          listingId={ad.id}
          listingTitle={ad.title}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}

function PriceOfferModal({ ad, onClose }) {
  const [form, setForm] = useState({
    sender_name: localStorage.getItem("stb_buyer_name") || "",
    sender_email: localStorage.getItem("stb_buyer_email") || "",
    sender_phone: "",
    offered_price: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const msg = form.message.trim() || `Nabízím za ${form.offered_price} Kč. Dejte vědět, prosím.`;
      const blocked = contactSpamReason({
        sender_name: form.sender_name,
        sender_email: form.sender_email,
        message: msg,
      });
      if (blocked) {
        setError(blocked);
        setBusy(false);
        return;
      }
      await api.post(`/listings/${ad.id}/contact`, {
        sender_name: form.sender_name.trim(),
        sender_email: form.sender_email.trim(),
        sender_phone: form.sender_phone.trim() || null,
        message: msg,
        offered_price: form.offered_price === "" ? null : Number(form.offered_price),
      });
      localStorage.setItem("stb_buyer_name", form.sender_name.trim());
      localStorage.setItem("stb_buyer_email", form.sender_email.trim());
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="price-offer-modal"
    >
      <div className="stb-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="stb-h text-xl text-white inline-flex items-center gap-2">
          <HandCoins size={18} className="text-[#FF6B00]" /> Nabídnout cenu
        </h3>
        {done ? (
          <div className="mt-4 text-center" data-testid="offer-done">
            <p className="stb-h text-[#22C55E] text-lg">Nabídka odeslána ✓</p>
            <p className="text-[#A3A3A3] text-sm mt-1">
              Prodejce dostal e-mail s tvojí nabídkou. Pokud bude souhlasit, ozve se ti.
            </p>
            <button onClick={onClose} className="stb-btn stb-btn-primary mt-4">Zavřít</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-3 space-y-2">
            <p className="text-xs text-[#A3A3A3]">
              Inzerát: <strong className="text-white">{ad.title}</strong>
            </p>
            <input
              type="number"
              min={0}
              className="stb-input text-2xl"
              placeholder="Tvoje nabídka v Kč"
              value={form.offered_price}
              onChange={(e) => setForm({ ...form, offered_price: e.target.value })}
              required
              data-testid="offer-price-amount"
            />
            <input
              className="stb-input"
              placeholder="Tvé jméno"
              value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              required maxLength={80}
              data-testid="offer-price-name"
            />
            <input
              className="stb-input"
              placeholder="Tvůj e-mail *"
              type="email"
              value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              required
              data-testid="offer-price-email"
            />
            <input
              className="stb-input"
              placeholder="Tvůj telefon (volitelné)"
              value={form.sender_phone}
              onChange={(e) => setForm({ ...form, sender_phone: e.target.value })}
              data-testid="offer-price-phone"
            />
            <textarea
              className="stb-input"
              rows={3}
              placeholder="Zpráva (volitelné) – proč právě tahle cena?"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              maxLength={2000}
              data-testid="offer-price-message"
            />
            {error && <p className="text-[#FF6B00] text-xs" data-testid="offer-price-error">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={busy} className="stb-btn stb-btn-primary flex-1" data-testid="offer-price-submit">
                {busy ? "Odesílám…" : "Odeslat nabídku"}
              </button>
              <button type="button" onClick={onClose} className="stb-btn stb-btn-ghost">Zrušit</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function AnonymousChatStart({ ad, onClose, navigate }) {
  const [form, setForm] = useState({
    buyer_name: localStorage.getItem("stb_buyer_name") || "",
    buyer_email: localStorage.getItem("stb_buyer_email") || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/chats/anonymous/start", {
        listing_id: ad.id,
        buyer_name: form.buyer_name.trim(),
        buyer_email: form.buyer_email.trim(),
      });
      localStorage.setItem("stb_buyer_name", form.buyer_name.trim());
      localStorage.setItem("stb_buyer_email", form.buyer_email.trim());
      localStorage.setItem(`stb_chat_token_${data.room_id}`, data.chat_token);
      navigate(`/zpravy/${data.room_id}`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="anon-chat-modal"
    >
      <div
        className="stb-card p-5 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="stb-h text-xl text-white inline-flex items-center gap-2">
          <MessageCircle size={18} className="text-[#FF6B00]" /> Začít chat s prodejcem
        </h3>
        <p className="text-xs text-[#A3A3A3] mt-1">
          Bez registrace. Stačí tvé jméno a e-mail – pomocí nich se k chatu vrátíš i příště.
        </p>
        <form onSubmit={submit} className="mt-3 space-y-2">
          <input
            className="stb-input"
            placeholder="Jméno"
            value={form.buyer_name}
            onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
            required
            maxLength={80}
            data-testid="anon-chat-name"
          />
          <input
            className="stb-input"
            placeholder="Tvůj e-mail"
            type="email"
            value={form.buyer_email}
            onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
            required
            data-testid="anon-chat-email"
          />
          {error && <p className="text-[#FF6B00] text-xs" data-testid="anon-chat-error">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              className="stb-btn stb-btn-primary flex-1"
              disabled={busy}
              data-testid="anon-chat-submit"
            >
              {busy ? "Otevírám…" : "Pokračovat do chatu"}
            </button>
            <button type="button" onClick={onClose} className="stb-btn stb-btn-ghost">Zrušit</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContactDialog({ ad }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sender_name: "", sender_email: "", sender_phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const name = localStorage.getItem("stb_buyer_name");
    const email = localStorage.getItem("stb_buyer_email");
    if (name || email) {
      setForm((f) => ({ ...f, sender_name: f.sender_name || name || "", sender_email: f.sender_email || email || "" }));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const blocked = contactSpamReason(form);
      if (blocked) {
        setError(blocked);
        setSending(false);
        return;
      }
      const { data } = await api.post(`/listings/${ad.id}/contact`, form);
      setDone(data.owner_email);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="stb-btn stb-btn-secondary w-full justify-center"
        data-testid="contact-email-toggle"
      >
        <Mail size={16} /> Odpovědět emailem
      </button>
    );
  }

  if (done) {
    const mailto = `mailto:${done}?subject=${encodeURIComponent(`Reakce na inzerát: ${ad.title}`)}&body=${encodeURIComponent(form.message)}`;
    return (
      <div className="space-y-2" data-testid="contact-success">
        <p className="stb-h text-[#22C55E] text-sm">Zpráva odeslána prodejci.</p>
        <p className="text-xs text-[#A3A3A3]">
          Prodejce ti odpoví na <span className="text-white">{form.sender_email}</span>.
        </p>
        <a className="stb-btn stb-btn-outline w-full justify-center" href={mailto} data-testid="contact-mailto">
          <Mail size={14} /> Otevřít v mém emailu
        </a>
        <button onClick={() => setOpen(false)} className="stb-btn stb-btn-ghost w-full">Zavřít</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="contact-email-form">
      <input
        className="stb-input"
        placeholder="Jméno"
        value={form.sender_name}
        onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
        required
        data-testid="contact-name"
      />
      <input
        className="stb-input"
        placeholder="Tvůj email"
        type="email"
        value={form.sender_email}
        onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
        required
        data-testid="contact-email"
      />
      <input
        className="stb-input"
        placeholder="Telefon (nepovinné)"
        value={form.sender_phone}
        onChange={(e) => setForm({ ...form, sender_phone: e.target.value })}
        data-testid="contact-phone"
      />
      <textarea
        className="stb-input"
        placeholder="Zpráva pro prodejce"
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        required
        data-testid="contact-message"
      />
      {error && <p className="text-[#FF6B00] text-xs" data-testid="contact-error">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" className="stb-btn stb-btn-primary flex-1" disabled={sending} data-testid="contact-submit">
          {sending ? "Odesílám…" : "Odeslat zprávu"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="stb-btn stb-btn-ghost">Zrušit</button>
      </div>
    </form>
  );
}

function TopButton({ ad, auth }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/top/checkout", {
        listing_id: ad.id,
        origin,
        auth,
      });
      window.location.href = data.url;
    } catch (e) {
      alert(apiError(e));
      setBusy(false);
    }
  };
  return (
    <button onClick={onClick} disabled={busy} className="stb-btn stb-btn-primary" data-testid="ad-top-btn-internal">
      <Zap size={14} /> {busy ? "Otevírám…" : "TOP za 49 Kč"}
    </button>
  );
}
